import { forwardRef, RefObject } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Button } from "src/components";
import { invoiceActions } from "src/context/invoice-slice";
import { RootState } from "src/context/store";
import FilterAccordion from "src/features/fields/container/FilterAccordion";
import { DateRange, DayPicker } from "react-day-picker";
import FilterAccordionBySearch from "./FilterAccordianBySearch";
import { toast } from "src/hooks/useToast";

interface FilterItem {
  id?: string;
  name?: string;
  code?: string | number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // Add an index signature
}

interface InvoiceApprovalFiltersProps {
  onClose: () => void;
  companyDetails: FilterItem[];
  divisionDetails: FilterItem[];
  stockistDetails: FilterItem[];
  statusDetails: FilterItem[];
  assigneeDetails: FilterItem[];
  cityDetails: FilterItem[];
  districtDetails: FilterItem[];
  stateDetails: FilterItem[];
  ref: RefObject<HTMLElement>;
}

const Divider = () => <div className="w-full h-[0.0625rem] my-2 bg-[#0000001a]" />;

const InvoiceApprovalFiltersComponent = forwardRef<HTMLElement, InvoiceApprovalFiltersProps>(
  ({ onClose, companyDetails, divisionDetails, statusDetails, assigneeDetails, cityDetails, districtDetails, stateDetails }, ref) => {
    // const [fuidNo, setFuidNo] = useState<string>("");
    const dispatch = useDispatch();
    const SORT_OPTIONS = [
      { key: "ASC", value: "Ascending" },
      { key: "DESC", value: "Descending" },
    ];

    const dateType = [
      { key: "entered_date", value: "Entered Date" },
      { key: "approved_date", value: "Approved Date" },
      { key: "file_upload_date", value: "File Upload Date" },
      { key: "ordered_date", value: "Order Placed Date" }
    ]

    // if (selected?.from && selected?.to) {
    //   dispatch(invoiceActions.setInvoiceFilters({ key: "dateRange", value: [selected.from?.toISOString(), selected.to?.toISOString()] }));
    // }

    // useEffect(() => {
    //   if (fuidNo) {
    //     dispatch(invoiceActions.setInvoiceFilters({ key: "fuidNo", value: [fuidNo] }));
    //   }
    // }, [fuidNo]);

    const companyFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "company", value: details }));
    };

    const divisionFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "division", value: details }));
    };

    const statusFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "status", value: details }));
    };

    const assigneeFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "assignee", value: details }));
    };

    const cityFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "city", value: details }));
    };

    const districtFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "district", value: details }));
    };

    const stateFilterHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "state", value: details }));
    };

    const sortFilerHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "sorting", value: details }));
    };

    // const dateRangeHandler = (details: DateRange | undefined) => {
    //   console.log({ details });
    //   if (details?.from && details?.to) {
    //     const payload: DateRange = {
    //       from: (new Date(details.from).toISOString() as any) || undefined,
    //       to: (new Date(details.to).toISOString() as any) || undefined,
    //     };
    //     dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "dateRange", value: payload }));
    //   }
    // };


    const dateTypeHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "dateType", value: [details] }));
    };

    const enteredByHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "enteredBy", value: details }));
    };

    const approvedByHandler = (details: FilterItem[]) => {
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "approvedBy", value: details }));
    };

    const resetFilter = () => {
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "company", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "division", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "stockist", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "status", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "assignee", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "city", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "district", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "state", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "fuidNo", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "sorting", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "dateRange", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "dateType", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "enteredBy", value: [] }));
      dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "approvedBy", value: [] }));
      dispatch(invoiceActions.setIsResetFilter(false));
    };
    const filters = useSelector((state: RootState) => state?.invoice?.InvoiceApprovalFilters);

    const dateRangeHandler = (details: DateRange | undefined) => {

      if (filters.dateType?.length === 0) {
        toast({
          description: "Please choose a date type first",
          variant: "destructive",
        })
        return;
      }
      if (details?.from) {
        const fromDate = new Date(details.from);
        const toDate = details.to ? new Date(details.to) : undefined;

        const isSameDate = toDate && fromDate.getTime() === toDate.getTime();

        const payload: DateRange = {
          from: fromDate.toISOString() as any,
          ...(toDate && !isSameDate && { to: toDate.toISOString() as any }),
        };
        dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "dateRange", value: payload }));
      }
    };

    return (
      <aside ref={ref} className="bg-white border-l-[1px] min-w-[24rem] overflow-y-auto max-w-[25rem] border-l-[#00000014] fixed px-8 text-[#49484C] text-xl font-semibold py-5 right-0  h-screen">
        <div className="flex items-center justify-between gap-5 mt-3">
          <h3 className="whitespace-nowrap">Fetch filters</h3>
          <Button className="border py-1 bg-[#7F56D9] text-white" onClick={resetFilter}>
            Reset
          </Button>
          <Button className="border py-2 bg-[#7F56D9] text-white" onClick={onClose}>
            Close
          </Button>
        </div>

        {/* filters  */}
        <div className="flex flex-col ">
          {/* <div className="mt-2">
            <h1 className="text-[.9rem] font-semibold">Assignee</h1>
            <Autocomplete
              placeholder="Search"
              suggestionList={assigneeDetails}
              displayKey="user_name"
              uniqueKey="invoice_assignment_id"
              onSelectionChange={assigneeFilterHandler}
              inputClassName="w-full"
              defaultValue={filters.assignee?.[0]?.user_name}
            />
          </div> */}
          <FilterAccordion
            uniqueKey="key"
            displayKey="value"
            onSelectingFilter={sortFilerHandler}
            title="Sort By"
            arrayToFilter={SORT_OPTIONS}
            defaultValue={filters.sorting}
            isSingleSelect={true}
          />
          <Divider />
          <FilterAccordion
            uniqueKey="invoice_assignment_id"
            displayKey="user_name"
            onSelectingFilter={assigneeFilterHandler}
            title="Assignee"
            arrayToFilter={assigneeDetails}
            defaultValue={filters.assignee}
          />
          <Divider />
          <FilterAccordionBySearch
            uniqueKey="entered_by"
            displayKey="entered_by"
            onSelectingFilter={enteredByHandler}
            title="Entered By"
            arrayToFilter={[]}
            defaultValue={filters.enteredBy}
          />
          <Divider />
          <FilterAccordionBySearch
            uniqueKey="approved_by"
            displayKey="approved_by"
            onSelectingFilter={approvedByHandler}
            title="Approved By"
            arrayToFilter={[]}
            defaultValue={filters.approvedBy}
          />
          <Divider />
          <FilterAccordion
            uniqueKey="code"
            displayKey="shortname"
            onSelectingFilter={companyFilterHandler}
            title="Company Name"
            arrayToFilter={companyDetails}
            defaultValue={filters.company}
          // isSingleSelect={true}
          />

          <Divider />
          <FilterAccordion
            uniqueKey="company_divisioncode"
            displayKey="division_name"
            onSelectingFilter={divisionFilterHandler}
            title="Division"
            arrayToFilter={divisionDetails}
            defaultValue={filters.division}
          // isSingleSelect={true}
          />
          <Divider />

          <FilterAccordion
            uniqueKey="fuid_status_id"
            displayKey="fuid_status_desc"
            title="Status"
            arrayToFilter={statusDetails}
            onSelectingFilter={statusFilterHandler}
            defaultValue={filters.status}
          // isSingleSelect={true}
          />

          <>
            <Divider />
            <FilterAccordion
              displayKey="city_name"
              title="City"
              arrayToFilter={cityDetails}
              uniqueKey="city_code"
              onSelectingFilter={cityFilterHandler}
              defaultValue={filters.city}
            // isSingleSelect={true}
            />
          </>
          <>
            <Divider />
            <FilterAccordion
              displayKey="district_name"
              title="District"
              arrayToFilter={districtDetails}
              uniqueKey="district_code"
              onSelectingFilter={districtFilterHandler}
              defaultValue={filters.district}
            // isSingleSelect={true}
            />
          </>
          <>
            <Divider />
            <FilterAccordion
              displayKey="state_name"
              title="State"
              arrayToFilter={stateDetails}
              uniqueKey="state_code"
              onSelectingFilter={stateFilterHandler}
              defaultValue={filters.state}
            // isSingleSelect={true}
            />
          </>
          <>
            <Divider />
            <FilterAccordion
              displayKey=""
              title="Date & Time"
              arrayToFilter={[]}
              uniqueKey=""
              onSelectingFilter={() => { }}
              defaultValue={[]}
            // isSingleSelect={true}
            />
          </>
          <div className="my-0">
            <div className="flex items-center gap-2 mb-2">
              <Autocomplete
                placeholder="Search"
                suggestionList={dateType}
                displayKey="value"
                uniqueKey="key"
                onSelectionChange={dateTypeHandler}
                defaultValue={filters.dateType?.[0]}
              />
              <Button
                className="bg-[#7F56D9] text-white w-[10rem]"
                onClick={() => {
                  dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "dateRange", value: [] }));
                  dispatch(invoiceActions.setInvoiceApprovalFilters({ key: "dateType", value: [] }));
                }}>Clear Date</Button>
            </div>
            <DayPicker
              classNames={{
                today: `border border-[#8051FF]`,
                selected: `bg-[#ded9ff] border-[#8051FF] font-semibold`, // Highlight the selected day
                root: `border border-[#D0D0D0] rounded-xl p-5 flex items-center justify-center text-[1rem]`, // Add a shadow to the root element
                day: `p-2 cursor-pointer`, // Base styles for each day
                chevron: `fill-[#7F56D9] hover:fill-[#8051FF] transition-colors`, // Change the color of the chevron
                outside: `text-gray-400`,
              }}
              mode="range"
              defaultMonth={filters.dateRange?.from}
              selected={filters.dateRange}
              onSelect={dateRangeHandler}
              numberOfMonths={1}
            />
          </div>
        </div>
      </aside >
    );
  }
);

export default InvoiceApprovalFiltersComponent;
